import { useEffect, useState, useCallback, useRef } from 'react';

/**
 * Custom hook that processes items in chunks with a delay between each chunk.
 *
 * @param {number} chunkSize - The number of items to include in each chunk
 * @returns {Object} An object containing:
 *   - {Array} chunkedItems - The current chunk of items being processed
 *   - {Function} addItems - Function to add items that will be processed in chunks
 *
 * @example
 * const { chunkedItems, addItems } = useChunking(10);
 *
 * Add 25 items - they will be processed as:
 * - First chunk of 10 items (immediately)
 * - Second chunk of 10 items (after 200ms)
 * - Third chunk of 5 items (after 400ms)
 * addItems([...25 items]);
 */
const useChunking = (chunkSize) => {
    const [chunkedItems, setChunkedItems] = useState([]);
    const timeoutRefsRef = useRef([]);

    /**
     * Adds items to be processed in chunks. Items are split into chunks of the specified size
     * and each chunk is emitted via setChunkedItems with a 200ms delay between chunks.
     */
    const addItems = useCallback(
        (items) => {
            if (!Array.isArray(items) || items.length === 0) {
                return;
            }

            // Emit chunks of items with 200ms delay between each
            const chunks = [];
            for (let i = 0; i < items.length; i += chunkSize) {
                chunks.push(items.slice(i, i + chunkSize));
            }

            chunks.forEach((chunk, index) => {
                const timeout = setTimeout(() => {
                    setChunkedItems(chunk);
                }, index * 200);
                timeoutRefsRef.current.push(timeout);
            });
        },
        [chunkSize]
    );

    const clearChunk = useCallback(() => {
        // Clear any pending timeouts
        timeoutRefsRef.current.forEach((timeout) => clearTimeout(timeout));
        timeoutRefsRef.current = [];
        setChunkedItems([]);
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            clearChunk();
        };
    }, [clearChunk]);

    return { chunkedItems, addItems };
};

export default useChunking;
